import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { adminApi } from "@/api/adminApi";
import { isApiError } from "@/api/errors";
import { onSessionExpired, setTokenProvider } from "@/api/http";
import { qk } from "@/api/queryKeys";
import { setUploadTokenProvider } from "@/api/testerApi";
import type { Role, User } from "@/api/types";
import { getAuthClient, SignInError, type AuthClient, type AuthUser } from "@/lib/auth";
import { SessionExpiredDialog } from "@/features/system/SessionExpiredDialog";

export type SessionStatus = "loading" | "signed_out" | "pending" | "active" | "no_access" | "identity_unavailable";

export interface Session {
  status: SessionStatus;
  user: AuthUser | null;
  me: User | null;
  role?: Role;
  adminApi: "online" | "unreachable";
  minutesLeft: number;
  signInError: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const ROLE_TTL = 5 * 60_000;
const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [client, setClient] = useState<AuthClient | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [me, setMe] = useState<User | null>(null);
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [adminState, setAdminState] = useState<"online" | "unreachable">("online");
  const [signInError, setSignInError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const lastOk = useRef(0);

  useEffect(() => {
    let off = () => undefined as unknown;
    void getAuthClient().then((c) => {
      setClient(c);
      setTokenProvider((force) => c.getIdToken(force));
      setUploadTokenProvider((force) => c.getIdToken(force));
      setUser(c.current());
      if (!c.current()) setStatus("signed_out");
      off = c.onChange((u) => {
        setUser(u);
        if (!u) {
          setMe(null);
          setStatus("signed_out");
          qc.clear();
        }
      });
    });
    return () => void off();
  }, [qc]);

  useEffect(() => {
    const off = onSessionExpired(() => setExpired(true));
    const onEvt = () => setExpired(true);
    window.addEventListener("ats:session-expired", onEvt);
    return () => {
      off();
      window.removeEventListener("ats:session-expired", onEvt);
    };
  }, []);

  const refreshMe = useCallback(async () => {
    if (!client?.current()) return;
    try {
      const m = await qc.fetchQuery({ queryKey: qk.me(), queryFn: adminApi.me, staleTime: 0 });
      lastOk.current = Date.now();
      setMe(m);
      setAdminState("online");
      setStatus(m.status === "active" ? "active" : m.status === "pending" ? "pending" : "no_access");
    } catch (e) {
      if (isApiError(e) && e.code === "NETWORK") {
        setAdminState("unreachable");
        if (lastOk.current === 0 || Date.now() - lastOk.current > ROLE_TTL) setStatus("identity_unavailable");
        return;
      }
      if (isApiError(e) && e.code === "FORBIDDEN") {
        setSignInError("Only @atatus.com accounts can use ATS.");
        await client.signOut();
        return;
      }
      if (isApiError(e) && e.code === "UNAUTHENTICATED") {
        setExpired(true);
        return;
      }
      setStatus("identity_unavailable");
    }
  }, [client, qc]);

  useEffect(() => {
    if (user) void refreshMe();
  }, [user, refreshMe]);

  useEffect(() => {
    const onFocus = () => void refreshMe();
    window.addEventListener("focus", onFocus);
    const t = setInterval(() => {
      setNow(Date.now());
      void refreshMe();
    }, 60_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  }, [refreshMe]);

  const value = useMemo<Session>(
    () => ({
      status,
      user,
      me,
      role: status === "active" ? (me?.role ?? undefined) : undefined,
      adminApi: adminState,
      minutesLeft: Math.max(0, Math.ceil((ROLE_TTL - (now - lastOk.current)) / 60_000)),
      signInError,
      async signIn() {
        setSignInError(null);
        try {
          await client?.signIn();
          setUser(client?.current() ?? null);
        } catch (e) {
          if (e instanceof SignInError && e.kind !== "popup_closed") setSignInError(e.message);
        }
      },
      async signOut() {
        await client?.signOut();
        setUser(null);
        setMe(null);
        setStatus("signed_out");
        qc.clear();
      },
      refreshMe,
    }),
    [status, user, me, adminState, now, signInError, client, refreshMe, qc],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <SessionExpiredDialog
        open={expired}
        onSignIn={async () => {
          await value.signIn();
          setExpired(false);
        }}
      />
    </Ctx.Provider>
  );
}

export function useSession(): Session {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}
